import React from "react";
import { GitCompareArrows, ShieldCheck } from "lucide-react";
import { cn } from "@/lib/utils";
import { money, ago } from "@/lib/sk";

export const sumText = (v, preliminary = false) => (v == null || (preliminary && !v) ? "–" : `${money(v, "NOK", 1, { bare: true })} kr`);

/** Who made it, when, and whether it is controlled – one quiet line under the version name. `short` fits the rail. */
export function versionMeta(r, short = false) {
  if (!r) return "";
  if (r.sent_at) {
    const to = r.sent_to?.name || r.sent_to?.company || "";
    const sw = short ? ago(r.sent_at).replace(/^for /, "").replace(/ siden$/, "") : ago(r.sent_at);
    return short ? ["Sendt", sw, to.split(" ")[0]].filter(Boolean).join(" · ") : [`Sendt${to ? ` til ${to}` : ""}`, sw].filter(Boolean).join(" · ");
  }
  const when = short ? ago(r.controlled_at || r.created_at).replace(/^for /, "").replace(/ siden$/, "") : ago(r.status === "kontrollert" ? r.controlled_at || r.created_at : r.created_at);
  const who = (r.status === "kontrollert" ? r.controlled_by?.name : r.created_by?.name) || "";
  if (short) return [r.status === "kontrollert" ? "Godkjent" : null, when, r.stale && r.status !== "kontrollert" ? null : who.split(" ")[0]].filter(Boolean).join(" · ");
  if (r.status === "kontrollert") return [`Godkjent${who ? ` av ${who}` : ""}`, when].filter(Boolean).join(" · ");
  return [`Laget ${when}`, who].filter(Boolean).join(" av ");
}

const DOT = {
  live: "bg-[hsl(var(--navy))]",
  ok: "bg-[hsl(var(--v-ok))]",
  stale: "bg-[hsl(var(--v-assumed))]",
  draft: "border-[1.5px] border-black/[0.28] bg-white",
};

/**
 * One version as one row: dot (navy = the live draft, green = controlled, amber = the calculation moved on, hollow = draft),
 * name, sum, and who/when. Used both in the left rail and in the picker under the state word.
 */
export function VersionRow({ r, live = false, active, liveTotals, onPick, onDiff, testId, short = false }) {
  const tone = live ? "live" : r.status === "kontrollert" ? "ok" : r.stale ? "stale" : "draft";
  const prelim = live ? Boolean(liveTotals?.preliminary) : Boolean(r?.preliminary);
  const sum = live ? sumText(liveTotals?.price, prelim) : sumText(r.totals_price, prelim);
  return (
    <li className="group/ver relative" data-testid={`${testId}-row`}>
      <button type="button" onClick={onPick} aria-current={active || undefined} className={cn("flex w-full items-start gap-2.5 rounded-[10px] px-2 py-2 text-left transition-colors hover:bg-black/[0.04]", active && "bg-black/[0.05]")} data-testid={testId} data-active={active ? "1" : "0"} data-tone={tone}>
        <span className={cn("mt-[6px] h-2 w-2 shrink-0 rounded-full", DOT[tone])} aria-hidden />
        <span className="min-w-0 flex-1">
          <span className="flex items-baseline justify-between gap-3">
            <span className={cn("inline-flex min-w-0 items-center gap-1 truncate text-[13.5px]", active ? "font-semibold text-foreground" : "font-medium text-foreground/85")}>
              {tone === "ok" ? <ShieldCheck className="h-3.5 w-3.5 shrink-0 text-[hsl(var(--v-ok))]" /> : null}{live ? "Utkast" : `Versjon ${r.revision}`}
            </span>
            <span className={cn("shrink-0 font-mono text-[12.5px] tabular-nums", prelim ? "text-[hsl(var(--v-assumed))]" : "text-foreground/70")} title={prelim ? "Foreløpig – ikke alt er priset" : undefined} data-testid={`${testId}-sum`}>{sum}</span>
          </span>
          <span className={cn("mt-0.5 block truncate text-[12px] text-muted-foreground", onDiff && "pr-7")}>
            {live ? "følger kalkylen" : versionMeta(r, short)}
            {!live && r.stale && r.status !== "kontrollert" ? <span className="text-[hsl(var(--v-assumed))]">{short ? " · endret etterpå" : " · kalkylen er endret etterpå"}</span> : null}
          </span>
        </span>
      </button>
      {onDiff ? (
        <button type="button" onClick={onDiff} className="absolute bottom-1.5 right-1.5 flex h-7 w-7 items-center justify-center rounded-[7px] text-muted-foreground opacity-0 transition-opacity hover:bg-black/[0.06] hover:text-foreground focus-visible:opacity-100 group-hover/ver:opacity-100" aria-label={`Endringer siden versjon ${r.revision}`} title="Endringer siden denne versjonen" data-testid={`${testId}-diff`}>
          <GitCompareArrows className="h-3.5 w-3.5" />
        </button>
      ) : null}
    </li>
  );
}

/** The versions as a timeline beside the paper: the live draft first, then every frozen version, newest on top. */
export function VersionRail({ revisions, sel, liveTotals, onPick, onDiff, className }) {
  return (
    <nav aria-label="Versjoner" className={cn("text-[12.5px]", className)} data-testid="tilbud-rail">
      <div className="k-label px-2">Versjoner</div>
      <ol className="mt-1.5 space-y-px">
        <VersionRow live active={sel === "live"} liveTotals={liveTotals} onPick={() => onPick("live")} testId="tilbud-rail-live" />
        {revisions.map((r) => <VersionRow key={r.id} r={r} active={sel === r.id} onPick={() => onPick(r.id)} onDiff={() => onDiff(r.revision)} testId={`tilbud-rail-rev-${r.revision}`} short />)}
      </ol>
      {!revisions.length ? <p className="px-2 pt-1.5 text-[12px] leading-[1.5] text-muted-foreground" data-testid="tilbud-rail-empty">Ingen versjoner ennå – «Lag versjon 1» låser tekst og priser slik de står.</p> : null}
    </nav>
  );
}
